/**
 * A person's enrolled faces, with the photos actually shown.
 *
 * ─── WHY IT EXISTS ───
 * The two dashboards each drew this list, and NEITHER showed an image — every row read
 * "Uploaded #2" with no way to see what had been enrolled. That is the one question this list
 * is for: a wrong, blurred or wrong-person photograph is invisible in a row of text, and it is
 * the reason a face stops matching.
 *
 * It is here rather than in the pages because the thing that used to be host-specific — how to
 * turn a photo id into something an <img> can load — is now `useEnrolledPhoto`, which resolves
 * through the injected API and therefore works on both.
 */
import { PhotoIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useEnrolledPhoto } from './useEnrolledPhoto';

export interface EnrolledFace {
  id: string;
  upload_order?: number;
  /** 'unknown_event' when linked from a sighting, 'upload' when somebody added it. */
  source?: string | null;
  look_direction?: string | null;
  [key: string]: unknown;
}

export interface EnrolledFacesListProps {
  faces: EnrolledFace[];
  loading?: boolean;
  canEdit?: boolean;
  /** Add more photos. Omit to hide the upload control. */
  onUpload?: (files: FileList | null) => void;
  onUnlink?: (photoId: string) => void;
}

/** One enrolled face: the photograph, where it came from, and a way to drop it. */
function FaceRow({ face, canEdit, onUnlink }: {
  face: EnrolledFace; canEdit?: boolean; onUnlink?: (id: string) => void;
}) {
  const src = useEnrolledPhoto(face.id);
  const from = face.source === 'unknown_event' ? 'From a sighting' : 'Uploaded';

  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted px-2 py-1.5">
      {src ? (
        <img
          src={src}
          alt=""
          className="h-10 w-10 shrink-0 rounded border border-border object-cover"
        />
      ) : (
        // Same footprint whether or not the image resolved, so a list where one photo is
        // missing still reads as one column of rows.
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-border bg-background">
          <PhotoIcon className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] text-foreground">{from}</p>
        <p className="truncate text-[10px] text-muted-foreground">
          #{face.upload_order ?? '?'}
          {face.look_direction ? ` · ${face.look_direction}` : ''}
        </p>
      </div>

      {canEdit && onUnlink && (
        <button
          type="button"
          onClick={() => onUnlink(face.id)}
          title="Unlink this face — the AI module drops its vector"
          aria-label="Unlink this face"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-500"
        >
          <TrashIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function EnrolledFacesList({
  faces, loading = false, canEdit = false, onUpload, onUnlink,
}: EnrolledFacesListProps) {
  return (
    <div className="space-y-1.5">
      {canEdit && onUpload && (
        <label className="flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-dashed border-border px-2 py-1.5 text-[11px] text-muted-foreground transition-colors hover:border-brand hover:text-foreground">
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            // The value is cleared so choosing the SAME file twice still fires a change —
            // re-uploading a photo after a failed attempt is an ordinary thing to do.
            onChange={(e) => { onUpload(e.target.files); e.target.value = ''; }}
          />
          <PlusIcon className="h-3.5 w-3.5" />
          Add photos (up to 5)
        </label>
      )}

      {loading ? (
        <div className="space-y-1.5">
          {[1, 2].map((i) => <div key={i} className="h-[52px] animate-pulse rounded-md bg-muted" />)}
        </div>
      ) : faces.length === 0 ? (
        <p className="py-2 text-center text-[11px] text-muted-foreground">
          No faces enrolled yet.
        </p>
      ) : (
        faces.map((f) => (
          <FaceRow key={f.id} face={f} canEdit={canEdit} onUnlink={onUnlink} />
        ))
      )}
    </div>
  );
}
